import { ReactNode, useMemo } from 'react';
import { Dialog } from '@insights-ltd/design-library/components';
import { ChaptersStatus } from 'api';
import PurchaseSummary from './PurchaseSummary';

export type PurchaseSummaryLine = {
  chapterName: string;
  unitCost: number;
  count: number;
};

export type BasePurchaseDialogProps = {
  open: boolean;
  onClose: () => void;
  onSubmit: () => void;
  chapterSummaries: PurchaseSummaryLine[];
  purchaseChapters: ChaptersStatus[];
  unitBalance: number;
  contributorsCount?: number;
  newContributors?: number;
  showNewContributors?: boolean;
  primaryButtonText: string;
  secondaryButtonText: string;
  title: string;
  extraContent?: ReactNode;
  disablePrimaryButtonWhenInvalid?: boolean;
  variant?: 'default' | 'dfc';
  disabled?: boolean;
};

const BasePurchaseDialog = ({
  open,
  onClose,
  onSubmit,
  chapterSummaries,
  purchaseChapters,
  unitBalance,
  contributorsCount,
  newContributors,
  showNewContributors,
  primaryButtonText,
  secondaryButtonText,
  title,
  extraContent,
  disablePrimaryButtonWhenInvalid = true,
  variant,
  disabled = false,
}: BasePurchaseDialogProps) => {
  const summaryLines = useMemo(() => {
    return chapterSummaries
      .map((summary) => ({
        ...summary,
        count: purchaseChapters.filter(
          ({ status, name }) =>
            status === 'PENDING' && name === summary.chapterName,
        ).length,
      }))
      .filter((sum) => sum.count > 0);
  }, [chapterSummaries, purchaseChapters]);

  const totalUnitCost = summaryLines.reduce(
    (acc, val) => acc + val.count * val.unitCost,
    0,
  );

  const invalidTransaction = unitBalance < totalUnitCost;

  return (
    <Dialog
      onSubmit={async () => {
        await onSubmit();
        onClose();
      }}
      onClose={onClose}
      open={open}
      content={
        <>
          {extraContent}
          <PurchaseSummary
            lines={summaryLines}
            contributorsCount={contributorsCount}
            unitBalance={unitBalance}
            totalUnitCost={totalUnitCost}
            invalidTransaction={invalidTransaction}
            newContributors={newContributors}
            showNewContributors={showNewContributors}
            variant={variant}
          />
        </>
      }
      primaryButtonText={primaryButtonText}
      secondaryButtonText={secondaryButtonText}
      title={title}
      disablePrimaryButton={
        disablePrimaryButtonWhenInvalid && (invalidTransaction || disabled)
      }
    />
  );
};

export default BasePurchaseDialog;
